import React, { useMemo } from 'react';
import PropTypes from 'prop-types';
import {
  BarController,
  BarElement,
  CategoryScale,
  Chart as ChartJS,
  Filler,
  LinearScale,
  LineController,
  LineElement,
  PointElement,
  TimeScale,
  Tooltip
} from 'chart.js';
import zoomPlugin from 'chartjs-plugin-zoom';
import { useTheme } from '@hooks';
import { simpleBarChartTooltip, updateMousePos } from '@components/Charts/ChartJsTooltipHelpers';
import { yAxisTitle, xAxisTitle } from '@components/ChartJsPlugins';
import { Box, Stack } from '@esgian/esgianui';
import Chartjs from '@components/Charts/Chartjs';
import { getStackedBarChartMax, parseNumberToSignificant } from '@helpers';

// Register Chart.js components
ChartJS.register(
  LineElement,
  PointElement,
  LineController,
  BarElement,
  BarController,
  CategoryScale,
  TimeScale,
  LinearScale,
  Filler,
  Tooltip,
  zoomPlugin
);

const getOptions = ({
  theme,
  data,
  max,
  min,
  unit,
  hideCurrentTooltip,
  secondaryYAxis,
  customTooltipHandler,
  calculateYMax,
  fixTooltipPosition,
  tooltipDatasetMaxHeightPx,
  positionTooltipAboveChart,
  positionTooltipAboveCursor,
  tooltipTitle,
  isHorizontal,
  limitLabelLength
}) => {
  const {
    palette: {
      text: { primary },
      neutral: { neutral04 }
    }
  } = theme;
  const isPercentage = unit === '%';
  let axisMax = max;
  if(isPercentage) {
    axisMax = 100;
  }
  else if (axisMax > 1000) {
    axisMax = axisMax * 1.1;
  }
  else if (axisMax >= 3) {
    if (calculateYMax) {
      axisMax = Math.ceil(axisMax / 5) * 5;
    }
  } else {
    axisMax = axisMax * 1.1;
  }

  // build category (labels) scale config
  const categoryScale = (() => {
    const maxRot = isHorizontal ? 0 : 45;
    const minRot = isHorizontal ? 0 : 45;
    const textAlign = isHorizontal ? 'right' : 'center';

    return {
      min: 0,
      offset: true,
      type: 'category',
      labels: data?.labels ?? [],
      ticks: {
        // horizontal bar rows must always show their label - autoSkip drops
        // labels when rows are dense, leaving bars misaligned with the axis
        autoSkip: !isHorizontal,
        // extra padding to move labels away from the bars
        padding: 8,
        color: primary,
        maxRotation: maxRot,
        minRotation: minRot,
        textAlign: textAlign,
        font: {
          size: 10,
          family: ['Roboto', 'helvetica', 'Arial', 'sans-serif'].join(',')
        },
        callback: function(value) {
          const label = this.getLabelForValue(value);
          if (typeof label === 'string' && label.length > 15 && limitLabelLength) {
            return label.substring(0, 13) + '...';
          }
          return label;
        }
      },
      grid: {
        display: false,
        drawOnChartArea: false,
        drawTicks: false
      }
    };
  })();

  // build numeric scale config
  const numericScale = (() =>{
    const maxRot = !isHorizontal ? 0 : 45;
    const minRot = !isHorizontal ? 0 : 45;
    const textAlign = !isHorizontal ? 'right' : 'center';
    const gridColour = !isHorizontal ? `${neutral04}7F` : '#ddddddff';

    return {
      border: {
        display: false
      },
      grid: {
        tickLength: 0,
        color: `${gridColour}`
      },
      afterFit: function (scaleInstance) {
        let stepSize = `${Math.round((axisMax * 1.5) / 5) * 4}`.length;
        let size = stepSize < 4 ? 4 : stepSize;
        scaleInstance.width = size * 8; // sets the width to 100px
      },
      ticks: {
        color: primary,
        maxRotation: maxRot,
        minRotation: minRot,
        textAlign: textAlign,
        font: {
          size: !isHorizontal ? 10 : 12,
          family: ['Roboto', 'helvetica', 'Arial', 'sans-serif'].join(','),
        },
        beginAtZero: true,
        callback: (val) => {
          if (isHorizontal)
            return parseNumberToSignificant(val)
          return axisMax >= 5 ? parseInt(val) : calculateYMax ? parseNumberToSignificant(val) : val;
        },
        maxTicksLimit: calculateYMax ? 6 : max + 1, // +1 for zero
        stepSize: Math.round(axisMax / 5),
        max: axisMax,
        count: calculateYMax ? 6 : max + 1,
        precision: 2,
        xMin: 0,
        autoSkip: false
      },
      min: min,
      max: axisMax,
      type: 'linear',
      position: 'left',
      beginAtZero: true
    };
  })();

  let options = {
    // set axis direction for horizontal bars when requested
    indexAxis: isHorizontal ? 'y' : 'x',
    commonUpdate: true,
    responsive: true,
    maintainAspectRatio: false,
    layout: {
      padding: {
        top: 30,
        // reserve internal right padding so the drawing area shrinks but canvas remains full width
        right: isHorizontal ? 40 : 0
      }
    },
    scales: isHorizontal ? { x: numericScale, y: categoryScale } : { x: categoryScale, y: numericScale },
    tooltips: {
      mode: 'index',
      intersect: false
    },
    plugins: {
      tooltip: (function() {
        // For horizontal charts we want the tooltip to show per-row (y axis).
        // Use index mode and set axis to 'y' so the tooltip groups by the category row.
        const interaction = isHorizontal ? { mode: 'index', axis: 'y', intersect: false } : { mode: 'index', intersect: false };
        return {
          callbacks: {},
          enabled: false,
          interaction,
          position: 'nearest',
          external: (context) =>
            customTooltipHandler
              ? customTooltipHandler(context, data)
              : simpleBarChartTooltip({
                  context: context,
                  data: data,
                  theme: theme,
                  hideCurrentTooltip: hideCurrentTooltip,
                  fixTooltipPosition: fixTooltipPosition,
                  datasetMaxHeightPx: tooltipDatasetMaxHeightPx,
                  positionAboveChart: positionTooltipAboveChart,
                  title: tooltipTitle,
                  positionAboveCursor: positionTooltipAboveCursor,
                  isHorizontal: isHorizontal
                })
        };
      })(),
      yAxisTitle: {
        fontColor: primary,
        fontSize: '12px',
        text: unit
      },
      xAxisTitle:{
        fontColor: 'primary',
        fontSize: '12px',
        text: unit
      },
      zoom: {
        pan: {
          enabled: true,
          mode: 'x'
        },
        limits: {
          x: { min: 'original', max: 'original', minRange: 10 }
        },
        zoom: {
          mode: 'x',
          onZoom({ chart }) {
            if (chart.scales.max < 10) {
              chart.scales.x.max = 10;
            }
          },
          drag: {
            enabled: false
          },
          pinch: {
            enabled: true
          },
          wheel: {
            enabled: true,
            modifierKey: 'ctrl'
          }
        }
      }
    }
  };
  if (secondaryYAxis) {
    options.scales.y1 = secondaryYAxis;
  }
  return options;
};

function SimpleChartJSBarChart({
  id,
  series,
  loading,
  unit,
  maxY,
  minY,
  chartRef,
  hideCurrentTooltip,
  customTooltipHandler,
  height,
  secondaryYAxis,
  type,
  stackedBar,
  calculateYMax,
  fixTooltipPosition,
  tooltipDatasetMaxHeightPx,
  positionTooltipAboveChart,
  positionTooltipAboveCursor,
  tooltipTitle,
  isHorizontal,
  limitLabelLength
}) {
  let axisTitlePlugIn = isHorizontal ? xAxisTitle : yAxisTitle;
  let plugins = [ axisTitlePlugIn ];
  const { theme, customScrollbar } = useTheme();
  const {
    palette: {
      charts: { sixColorsLine }
    }
  } = theme;

  const data = useMemo(() => {
    if (!series.length) return { labels: [], datasets: [] };
    const labels = series.map(s => s.label);
    const values = series.map(s => s.data?.[0]?.y);
    const backgroundColours = series.map(s => s.backgroundColor);
    return {
      labels: labels,
      datasets: [{
        borderColor: sixColorsLine[0 % sixColorsLine.length], // Use modulo to avoid index out of bounds
        borderWidth: 0,
        spanGaps: true,
        pointHitRadius: 0,
        pointHoverRadius: 0,
        tension: 0,
        unit: series[0].unit,
        label: tooltipTitle ?? '',
        backgroundColor: backgroundColours,
        barPercentage: series[0].barPercentage || 0.9,
        data: values ?? [] // Pass data correctly
      }]
    };
  }, [series, sixColorsLine]);

  const options = useMemo(() => {
    if (!data.datasets.length) return null;

    let maxYaxis = maxY;
    if (!maxYaxis) {
      if (stackedBar) {
        maxYaxis = getStackedBarChartMax(series);
      } else {
        maxYaxis = Math.max(...(series.flatMap((s) => s.data.map((d) => d.y)) ?? 1));
      }
    }

    return getOptions({
      theme: theme,
      data: data,
      max: maxYaxis,
      min: minY,
      unit: unit,
      hideCurrentTooltip: hideCurrentTooltip,
      secondaryYAxis: secondaryYAxis,
      customTooltipHandler: customTooltipHandler,
      calculateYMax: calculateYMax,
      fixTooltipPosition: fixTooltipPosition,
      tooltipDatasetMaxHeightPx: tooltipDatasetMaxHeightPx,
      positionTooltipAboveChart: positionTooltipAboveChart,
      customScrollbarStyle: customScrollbar,
      tooltipTitle: tooltipTitle,
      positionTooltipAboveCursor: positionTooltipAboveCursor,
      isHorizontal: isHorizontal,
      limitLabelLength: limitLabelLength
    });
  }, [data, theme]);

  return (
    <Stack sx={{ width: 'auto' }} spacing={1}>
  {/* Keep canvas full width; reserve internal right padding for rotated charts */}
  <Box sx={{ height: height, width: '100%', position: 'relative' }}>
        <Chartjs
          loading={loading}
          plugins={plugins}
          chartRef={chartRef}
          id={id}
          type={'bar'}
          data={data}
          options={options}
          onMouseMove={updateMousePos}
          sx={{ ...customScrollbar }}
        />
      </Box>
    </Stack>
  );
}

SimpleChartJSBarChart.propTypes = {
  loading: PropTypes.bool,
  hideCurrentTooltip: PropTypes.bool,
  height: PropTypes.string,
  maxY: PropTypes.number,
  secondaryYAxis: PropTypes.object,
  minY: PropTypes.number,
  unit: PropTypes.string,
  series: PropTypes.arrayOf(PropTypes.object),
  id: PropTypes.string,
  chartRef: PropTypes.oneOfType([PropTypes.func, PropTypes.shape({ current: PropTypes.any })]),
  customTooltipHandler: PropTypes.func,
  calculateYMax: PropTypes.bool,
  fixTooltipPosition: PropTypes.bool,
  tooltipDatasetMaxHeightPx: PropTypes.number,
  positionTooltipAboveChart: PropTypes.bool,
  positionTooltipAboveCursor: PropTypes.bool,
  tooltipTitle: PropTypes.string,
  isHorizontal: PropTypes.bool,
  limitLabelLength: PropTypes.bool
};

SimpleChartJSBarChart.defaultProps = {
  loading: false,
  height: '330px',
  unit: '',
  maxY: undefined,
  secondaryYAxis: undefined,
  minY: 0,
  series: [],
  id: 'simpleBarChart',
  chartRef: undefined,
  hideCurrentTooltip: false,
  customTooltipHandler: undefined,
  calculateYMax: true,
  fixTooltipPosition: false,
  tooltipDatasetMaxHeightPx: undefined,
  positionTooltipAboveChart: false,
  positionTooltipAboveChart: false,
  tooltipTitle: undefined,
  isHorizontal: false,
  limitLabelLength: true
};

export default SimpleChartJSBarChart;